import React from 'react';
import { motion } from 'framer-motion';

interface CreateAdvisorModalProps {
  show: boolean;
  onClose: () => void;
  formData: {
    name: string;
    expertise: string;
    description: string;
    gender: 'male' | 'female';
  };
  onFormChange: (field: string, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading?: boolean;
}

export const CreateAdvisorModal: React.FC<CreateAdvisorModalProps> = ({
  show,
  onClose,
  formData,
  onFormChange,
  onSubmit,
  isLoading = false
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-2xl bg-black/60 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-white/20"
      >
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Create Your New Advisor</h3>
        <p className="text-white/70 mb-6">Tell us about your ideal advisor and we'll create their complete persona for you.</p>
        
        <form onSubmit={onSubmit} className="space-y-4 sm:space-y-6">
          <div>
            <label className="block text-white font-semibold mb-2">Advisor Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => onFormChange('name', e.target.value)}
              placeholder="e.g., Dr. Sarah Chen"
              className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-purple-400"
              required
            />
          </div>
          
          <div>
            <label className="block text-white font-semibold mb-2">Gender</label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="gender"
                  value="male"
                  checked={formData.gender === 'male'}
                  onChange={(e) => onFormChange('gender', e.target.value)}
                  className="mr-2"
                />
                <span className="text-white">Male</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="gender"
                  value="female"
                  checked={formData.gender === 'female'}
                  onChange={(e) => onFormChange('gender', e.target.value)}
                  className="mr-2"
                />
                <span className="text-white">Female</span>
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-white font-semibold mb-2">Area of Expertise</label>
            <input
              type="text"
              value={formData.expertise}
              onChange={(e) => onFormChange('expertise', e.target.value)}
              placeholder="e.g., Tech Leadership, Financial Planning, Health & Wellness"
              className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-purple-400"
              required
            />
          </div>
          
          <div>
            <label className="block text-white font-semibold mb-2">What makes them unique?</label>
            <textarea
              value={formData.description}
              onChange={(e) => onFormChange('description', e.target.value)}
              placeholder="Describe their personality, approach, or what specific perspective they bring..."
              rows={3}
              className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
              required
            />
          </div>
          
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`flex-1 px-4 sm:px-6 py-3 rounded-2xl transition-all duration-300 ${
                isLoading ? 'bg-white/5 text-white/50 cursor-not-allowed' : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`flex-1 px-4 sm:px-6 py-3 rounded-2xl transition-all duration-300 ${
                isLoading 
                  ? 'bg-white/10 text-white/50 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-500 to-orange-500 text-white hover:from-purple-400 hover:to-orange-400'
              }`}
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white animate-spin rounded-full" />
                  Creating...
                </div>
              ) : (
                'Create Advisor'
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};